// Types for Shift Templates (Modelos de Turno)

export interface DaySpecificTime {
  start: string; // HH:MM format
  end: string;   // HH:MM format
}

// JSONB format: { "0": { "start": "07:00", "end": "13:00" }, "6": { "start": "07:00", "end": "13:00" } }
// Where 0 = Sunday, 6 = Saturday
export type DaySpecificTimes = Record<string, DaySpecificTime>;

export type AlarmSound = 'bell' | 'double_beep' | 'xylophone' | 'rising' | 'marimba';

export interface ShiftTemplate {
  id: string;
  patient_id: string;
  name: string;
  default_start_time: string; // TIME format HH:MM:SS
  default_end_time: string;   // TIME format HH:MM:SS
  day_specific_times: DaySpecificTimes | null;
  has_variable_hours: boolean;
  is_overnight: boolean;
  auto_rollover?: boolean | null;
  alarm_enabled?: boolean | null;
  alarm_sound?: AlarmSound | string | null;
  alarm_vibrate?: boolean | null;
  alarm_repeat_minutes?: number | null;
  alarm_max_repeats?: number | null;
  created_at: string;
  updated_at: string;
}

export interface CreateShiftTemplateInput {
  patient_id: string;
  name: string;
  default_start_time: string;
  default_end_time: string;
  day_specific_times?: DaySpecificTimes | null;
  has_variable_hours?: boolean;
  is_overnight?: boolean;
  auto_rollover?: boolean;
  alarm_enabled?: boolean;
  alarm_sound?: string;
  alarm_vibrate?: boolean;
  alarm_repeat_minutes?: number;
  alarm_max_repeats?: number;
}

export interface UpdateShiftTemplateInput {
  name?: string;
  default_start_time?: string;
  default_end_time?: string;
  day_specific_times?: DaySpecificTimes | null;
  has_variable_hours?: boolean;
  is_overnight?: boolean;
  auto_rollover?: boolean;
  alarm_enabled?: boolean;
  alarm_sound?: string;
  alarm_vibrate?: boolean;
  alarm_repeat_minutes?: number;
  alarm_max_repeats?: number;
}


// Day of week constants
export const DAYS_OF_WEEK = [
  { value: 0, label: 'Domingo', short: 'Dom' },
  { value: 1, label: 'Segunda', short: 'Seg' },
  { value: 2, label: 'Terça', short: 'Ter' },
  { value: 3, label: 'Quarta', short: 'Qua' },
  { value: 4, label: 'Quinta', short: 'Qui' },
  { value: 5, label: 'Sexta', short: 'Sex' },
  { value: 6, label: 'Sábado', short: 'Sáb' },
] as const;

// Helper to format time for display (HH:MM:SS -> HH:MM)
export function formatTimeDisplay(time: string): string {
  return time.substring(0, 5);
}

// Helper to get effective shift hours for a specific day
export function getShiftHoursForDay(
  template: ShiftTemplate,
  dayOfWeek: number
): { start: string; end: string } | null {
  // Check if there's a specific time for this day
  if (template.has_variable_hours && template.day_specific_times) {
    const dayKey = String(dayOfWeek);
    const daySpecific = template.day_specific_times[dayKey];
    
    if (daySpecific) {
      return daySpecific;
    }
  }
  
  // Return default times
  return {
    start: formatTimeDisplay(template.default_start_time),
    end: formatTimeDisplay(template.default_end_time),
  };
}

// Helper to check if a time falls within shift hours
export function isTimeInShift(
  time: string, // HH:MM format
  shiftStart: string,
  shiftEnd: string,
  isOvernight: boolean
): boolean {
  const timeMinutes = timeToMinutes(time);
  const startMinutes = timeToMinutes(shiftStart);
  const endMinutes = timeToMinutes(shiftEnd);
  
  if (isOvernight) {
    // For overnight shifts (e.g., 19:00 - 07:00)
    // Time is valid if it's >= start OR < end
    return timeMinutes >= startMinutes || timeMinutes < endMinutes;
  } else {
    // For regular shifts (e.g., 07:00 - 19:00)
    return timeMinutes >= startMinutes && timeMinutes < endMinutes;
  }
}

function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

// Caregiver with shift template info
export interface CaregiverWithTemplate {
  user_id: string;
  full_name: string | null;
  avatar_url: string | null;
  shift_template_id: string | null;
  shift_template?: ShiftTemplate | null;
}
